import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import StickyBottomNav from "../../../components/Navbar/StickyBottomNav";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { FolderOpen, Image, AlertCircle } from "lucide-react";
import "react-lazy-load-image-component/src/effects/blur.css";

const EventGallery = () => {
  const [folders, setFolders] = useState([]);
  const [folderImages, setFolderImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  const cache = new Map();

  const fetchFolderImages = async (folderId) => {
    if (cache.has(folderId)) {
      setFolderImages(prev => ({
        ...prev,
        [folderId]: cache.get(folderId)
      }));
      return;
    }

    try {
      const response = await fetch(
        `${apiUrl}/api/drive-photos?folderID=${folderId}&size=thumbnail`
      );
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      cache.set(folderId, data);
      setFolderImages(prev => ({
        ...prev,
        [folderId]: data
      }));
    } catch (error) {
      console.error("Error fetching folder images:", error);
      setError("Failed to load images");
    }
  };

  const fetchFolders = useCallback(async () => {
    try {
      const response = await fetch(`${apiUrl}/api/drive-folders`);
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setFolders(data);
      await Promise.all(data.map(folder => fetchFolderImages(folder.id)));
    } catch (error) {
      console.error("Error fetching folders:", error);
      setError("Failed to load folders");
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  return (
    <div className="min-h-screen bg-lightTeal">
      <Navbar />
      
      <div className="py-16 px-6 md:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-display text-h1 text-secondary mb-4">Event Gallery</h1>
            <p className="text-bodyLarge text-textPrimary max-w-2xl mx-auto mb-4">
              Browse through photos from our community events and activities
            </p>
            <div className="w-16 h-1 bg-cta mx-auto"></div>
          </motion.div>
          
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-6"></div>
              <p className="text-textPrimary">Loading galleries...</p>
            </div>
          ) : error ? (
            <motion.div 
              className="bg-white rounded-xl p-8 shadow-md max-w-md mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertCircle size={32} className="text-red-500" />
              </div>
              <h3 className="font-display text-h3 text-secondary mb-2">Something went wrong</h3>
              <p className="text-textPrimary mb-4">{error}</p>
              <button 
                onClick={fetchFolders}
                className="bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary/90 transition-colors"
              >
                Try Again
              </button>
            </motion.div>
          ) : folders.length === 0 ? (
            <motion.div 
              className="bg-white rounded-xl p-8 shadow-md max-w-md mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-16 h-16 mx-auto rounded-full bg-lightTeal flex items-center justify-center mb-4">
                <FolderOpen size={32} className="text-primary" />
              </div>
              <h3 className="font-display text-h3 text-secondary mb-2">No Events Found</h3>
              <p className="text-textPrimary">Event galleries will appear here as they become available.</p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {folders.map((folder, index) => (
                <motion.div
                  key={folder.id}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  whileHover={{ y: -5 }}
                >
                  <Link
                    to={`/gallery/events/${encodeURIComponent(folder.name)}`}
                    state={{ folderId: folder.id }}
                    className="block h-full"
                  >
                    <div className="h-48 overflow-hidden relative">
                      {folderImages[folder.id]?.length > 0 ? (
                        <LazyLoadImage
                          alt={folder.name}
                          effect="blur"
                          src={`${apiUrl}${folderImages[folder.id][0].thumbnailUrl}`}
                          className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-105"
                          placeholderSrc="/placeholder-image.jpg"
                        />
                      ) : (
                        <div className="w-full h-full bg-lightTeal flex items-center justify-center">
                          <Image size={48} className="text-primary opacity-50" />
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <h3 className="font-display text-h3 text-secondary mb-2 line-clamp-1">
                        {folder.name}
                      </h3>
                      <div className="flex items-center text-textPrimary">
                        <Image size={16} className="mr-2" />
                        <span>{folderImages[folder.id]?.length || 0} images</span>
                      </div>
                      <div className="w-12 h-1 bg-cta mt-4"></div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <Footer />
      <StickyBottomNav />
    </div>
  );
};

export default EventGallery;